import React from 'react';
import { truncateCompanyScriptNameForBox } from '../utils/company/company-logo-fallback-content';
import {
  computeCompanyLogoScriptFontFloor,
  computeCompanyLogoScriptFontPxInitial,
  fitCompanyLogoScriptFontPx,
  scriptNameLineClamp,
} from '../utils/company/company-logo-script-font-metrics';

export type CompanyLogoScriptTheme = 'green' | 'purple';

export interface CompanyLogoScriptFallbackProps {
  sizePx: number;
  variant: 'default' | 'rounded' | 'square';
  /** Company name for the condensed title (brand part; legal form is separate row). */
  scriptLine: string;
  formLabel: string;
  theme?: CompanyLogoScriptTheme;
  className?: string;
  'aria-label'?: string;
}

const PURPLE_BG =
  'linear-gradient(152deg, #3b0764 0%, #4c1d95 28%, #6B46C1 52%, #7c3aed 78%, #8b5cf6 100%)';

function borderRadiusForVariant(variant: CompanyLogoScriptFallbackProps['variant']): string {
  if (variant === 'square') return '0';
  if (variant === 'rounded') return '0.75rem';
  return '1rem';
}

/**
 * Text-only logo substitute: Pacifico script name (word-wrapped) + serif legal form.
 * Font size is measured after layout and stepped down until the text fits the tile.
 */
export const CompanyLogoScriptFallback: React.FC<CompanyLogoScriptFallbackProps> = ({
  sizePx,
  variant,
  scriptLine,
  formLabel,
  theme = 'green',
  className = '',
  'aria-label': ariaLabel,
}) => {
  const r = borderRadiusForVariant(variant);
  const trimmedForm = formLabel.trim();
  const showForm = trimmedForm.length > 0 && sizePx >= 34;
  const showDivider = showForm && sizePx >= 40;

  const displayScript = truncateCompanyScriptNameForBox(scriptLine.trim() || '?', sizePx, showForm);
  const nameLines = scriptNameLineClamp(sizePx);
  const initialScriptPx = computeCompanyLogoScriptFontPxInitial(sizePx, showForm, nameLines);
  const floorPx = computeCompanyLogoScriptFontFloor(sizePx);
  const formPx = Math.max(5, Math.min(Math.round(sizePx * 0.11), 11));

  const nameBandRef = React.useRef<HTMLDivElement>(null);
  const nameSpanRef = React.useRef<HTMLSpanElement>(null);
  const [scriptPx, setScriptPx] = React.useState(initialScriptPx);

  React.useLayoutEffect(() => {
    let cancelled = false;

    const measureAndFit = () => {
      if (cancelled) return;
      const span = nameSpanRef.current;
      const band = nameBandRef.current;
      if (!span || !band) {
        setScriptPx(initialScriptPx);
        return;
      }
      const fitted = fitCompanyLogoScriptFontPx(initialScriptPx, floorPx, (px) => {
        span.style.fontSize = `${px}px`;
        void span.offsetHeight;
        return {
          scrollHeight: span.scrollHeight,
          clientHeight: span.clientHeight,
          scrollWidth: span.scrollWidth,
          clientWidth: span.clientWidth,
          offsetHeight: span.offsetHeight,
          bandClientHeight: band.clientHeight,
        };
      });
      setScriptPx(fitted);
    };

    measureAndFit();
    const fontsReady = typeof document !== 'undefined' ? document.fonts?.ready : undefined;
    if (fontsReady && typeof fontsReady.then === 'function') {
      void fontsReady.then(() => {
        if (!cancelled) measureAndFit();
      });
    }

    return () => {
      cancelled = true;
    };
  }, [displayScript, initialScriptPx, floorPx, sizePx, showForm, nameLines]);

  const isPurple = theme === 'purple';

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-theme={theme}
      className={`company-logo-script-fallback-root flex flex-col ${!isPurple ? 'company-logo-script-fallback--green-bg' : ''} ${className}`}
      style={{
        width: sizePx,
        height: sizePx,
        borderRadius: r,
        ...(isPurple
          ? {
              background: PURPLE_BG,
              boxShadow: '0 2px 10px 0 rgba(76, 29, 149, 0.45)',
            }
          : {
              boxShadow: '0 2px 10px 0 rgba(26, 46, 34, 0.35)',
            }),
      }}
    >
      <div
        ref={nameBandRef}
        className="flex min-h-0 flex-1 flex-col items-center justify-center px-[3px] pt-[3px]"
      >
        <span
          ref={nameSpanRef}
          className="company-logo-script-fallback__name text-center"
          style={{
            fontSize: scriptPx,
            WebkitLineClamp: nameLines,
            lineClamp: nameLines,
          }}
        >
          {displayScript}
        </span>
      </div>

      {showForm ? (
        <div className="flex w-full flex-shrink-0 flex-col items-center px-0.5 pb-[5px] pt-0">
          {showDivider ? <div className="company-logo-script-fallback__divider" aria-hidden /> : null}
          <span className="company-logo-script-fallback__form" style={{ fontSize: formPx }}>
            {trimmedForm}
          </span>
        </div>
      ) : null}
    </div>
  );
};
